import React, { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export interface DuplicateCandidate {
  individual: {
    id: string;
    first_name?: string;
    middle_names?: string;
    surname?: string;
    dob?: string;
    birth_year?: number;
    birth_place?: string;
  };
  score: number;
  reasons: string[];
}

// An add-form save handler, and its arguments kept so "create anyway" can replay them
export type SaveIndividualHandler = (
  ind: any,
  bioFatherId: string | null,
  bioMotherId: string | null,
  spouseId: string | null,
  adoptiveFatherId: string | null,
  adoptiveMotherId: string | null,
  allowDuplicate?: boolean,
) => void;
export type PendingSave = Parameters<SaveIndividualHandler>;

export function candidateName(c: DuplicateCandidate): string {
  const ind = c.individual;
  return [ind.first_name, ind.middle_names, ind.surname].filter(Boolean).join(' ') || 'Unknown';
}

interface DuplicateSuggestionsProps {
  mode: 'add' | 'edit';
  // add mode: candidates returned by a 409 from POST /individuals
  candidates?: DuplicateCandidate[];
  onCreateAnyway?: () => void;
  onOpenExisting?: (id: string) => void;
  // edit mode: the saved individual, looked up and offered for merge
  individualId?: string;
  onMerged?: (keptId: string) => void;
}

const DuplicateSuggestions: React.FC<DuplicateSuggestionsProps> = ({
  mode,
  candidates,
  onCreateAnyway,
  onOpenExisting,
  individualId,
  onMerged,
}) => {
  const [found, setFound] = useState<DuplicateCandidate[]>([]);
  const [mergeError, setMergeError] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    setFound([]);
    setMergeError(null);
    if (mode !== 'edit' || !individualId) return;
    let cancelled = false;
    fetch(`${API_URL}/individuals/${individualId}/duplicates`)
      .then(r => (r.ok ? r.json() : { candidates: [] }))
      .then(data => { if (!cancelled) setFound(data.candidates || []); })
      .catch(() => { /* suggestions are optional */ });
    return () => { cancelled = true; };
  }, [mode, individualId]);

  const list = mode === 'add' ? candidates || [] : found;
  if (list.length === 0) return null;

  const handleMerge = async (c: DuplicateCandidate) => {
    const name = candidateName(c);
    if (!individualId || !window.confirm(
      `Merge this record into ${name}? This record will be removed, and its relationships, DNA segments and notes will move to ${name}.`
    )) return;
    setMerging(true);
    setMergeError(null);
    try {
      const res = await fetch(`${API_URL}/individuals/merge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ merge_from_id: individualId, merge_to_id: c.individual.id }),
      });
      const data = await res.json();
      if (!res.ok) {
        setMergeError(data.detail || 'Merge failed');
        return;
      }
      onMerged?.(c.individual.id);
    } catch (e) {
      setMergeError('Merge failed');
    } finally {
      setMerging(false);
    }
  };

  return (
    <div className="genealogy-duplicate-suggestions">
      <div className="genealogy-duplicate-suggestions-title">
        {mode === 'add' ? 'This may be someone already in the tree:' : 'Possible duplicates of this person:'}
      </div>
      <ul>
        {list.map(c => (
          <li key={c.individual.id}>
            <div>
              <strong>{candidateName(c)}</strong>
              {c.individual.birth_year ? ` (b. ${c.individual.birth_year})` : c.individual.dob ? ` (b. ${c.individual.dob})` : ''}
              <span className="genealogy-duplicate-score">{Math.round(c.score * 100)}% match</span>
            </div>
            {c.reasons.length > 0 && <div className="genealogy-duplicate-reasons">{c.reasons.join(' · ')}</div>}
            {mode === 'add' && onOpenExisting && (
              <button type="button" className="genealogy-btn-secondary" onClick={() => onOpenExisting(c.individual.id)}>
                Open existing
              </button>
            )}
            {mode === 'edit' && (
              <button type="button" className="genealogy-btn-secondary" disabled={merging} onClick={() => handleMerge(c)}>
                Merge into this
              </button>
            )}
          </li>
        ))}
      </ul>
      {mergeError && <div className="genealogy-duplicate-error">{mergeError}</div>}
      {mode === 'add' && onCreateAnyway && (
        <button type="button" className="genealogy-btn-primary" onClick={onCreateAnyway}>
          Not the same person, create anyway
        </button>
      )}
    </div>
  );
};

export default DuplicateSuggestions;
